import { Link } from "react-router-dom";
import { describePhase1Progress } from "../lib/phase1Progress";
import { phase1CurrentActivityLine, phase1OverallPercent } from "../lib/phase1OverallPercent";
import type { Phase1JobPoll, ScrapeStep } from "../lib/phase1Job";
import { Phase1ProgressBar } from "./Phase1ProgressBar";

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="p-2 bg-surface-container rounded border border-outline-variant">
      <p className="text-[10px] uppercase text-on-surface-variant">{label}</p>
      <p className="text-lg font-semibold text-on-surface">{value}</p>
    </div>
  );
}

type Props = {
  runId: string;
  targetName?: string;
  jobStatus?: string;
  statusDetail?: string;
  progress: Phase1JobPoll["progress"] | null;
  liveLog: Phase1JobPoll["log"] | null;
  scrapeSteps: ScrapeStep[];
  error?: string;
  className?: string;
};

export function Phase1ExecuteProgress({
  runId,
  targetName = "—",
  jobStatus = "",
  statusDetail = "",
  progress,
  liveLog,
  scrapeSteps,
  error,
  className = "",
}: Props) {
  const copy = describePhase1Progress({
    status: jobStatus,
    status_detail: statusDetail,
    progress,
    log: liveLog,
  });
  const queriesQueued = Number(progress?.queries_queued ?? 0);
  const queriesDone = Number(progress?.queries_done ?? progress?.steps_total ?? 0);
  const scrapeTotal = Number(progress?.scrape_total || progress?.urls_discovered || 0);
  const scrapeDone = Number(progress?.scrape_done || 0);
  const overallPct = phase1OverallPercent(progress);
  const activityLine =
    copy.activityLine || phase1CurrentActivityLine(progress) || copy.detail;
  const isScraping =
    progress?.phase === "scraping" ||
    (scrapeTotal > 0 && scrapeDone < scrapeTotal);
  const isDiscovering =
    progress?.phase === "discovering" ||
    (queriesQueued > 0 && queriesDone < queriesQueued);
  const isQueued = copy.phaseLabel === "queued";
  const queryPct =
    queriesQueued > 0 ? Math.min(100, Math.round((queriesDone / queriesQueued) * 100)) : 0;
  const scrapePct =
    scrapeTotal > 0 ? Math.min(100, Math.round((scrapeDone / scrapeTotal) * 100)) : 0;

  if (error) {
    return (
      <section
        className={`flex flex-col flex-1 min-h-0 p-6 panel-border rounded-lg bg-surface-container border-error/40 ${className}`}
      >
        <p className="font-semibold text-error">Phase 1 failed</p>
        <p className="text-sm text-on-surface-variant mt-2 font-data-mono">{error}</p>
        <Link
          to={`/logs?run=${encodeURIComponent(runId)}`}
          className="text-primary text-xs hover:underline mt-4 inline-block"
        >
          View full log
        </Link>
      </section>
    );
  }

  return (
    <section
      className={`flex flex-col flex-1 min-h-0 p-6 panel-border rounded-lg bg-surface-container border-primary/20 ${className}`}
    >
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap shrink-0">
        <div className="flex items-center gap-3">
          {!isQueued && <span className="orion-spinner w-6 h-6" />}
          <div>
            <p className="font-semibold text-on-surface">{copy.headline}</p>
            <p className="text-body-sm text-on-surface-variant">
              {targetName !== "—" ? `Target: ${targetName} · ` : ""}
              {activityLine}
            </p>
          </div>
        </div>
        <Link
          to={`/logs?run=${encodeURIComponent(runId)}`}
          className="text-primary text-xs hover:underline shrink-0"
        >
          Full log
        </Link>
      </div>

      {!isQueued && (
        <Phase1ProgressBar
          className="mb-4 shrink-0"
          percent={overallPct}
          label="Overall progress"
          sublabel={
            queriesQueued + scrapeTotal > 0
              ? `${queriesDone + scrapeDone} of ${queriesQueued + scrapeTotal} steps (searches + page downloads)`
              : undefined
          }
        />
      )}

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-center mb-4 shrink-0">
        <Stat label="Phase" value={copy.phaseLabel} />
        <Stat
          label="Queries"
          value={
            queriesQueued > 0 ? `${queriesDone} / ${queriesQueued}` : progress?.steps_total ?? 0
          }
        />
        <Stat label="URLs" value={progress?.urls_discovered ?? "—"} />
        <Stat
          label="Downloaded"
          value={
            scrapeTotal > 0 ? `${scrapeDone} / ${scrapeTotal}` : progress?.firecrawl_scraped ?? "—"
          }
        />
        <Stat label="Errors" value={progress?.errors ?? 0} />
      </div>

      {isDiscovering && queriesQueued > 0 && (
        <Phase1ProgressBar
          className="mb-3 shrink-0"
          percent={queryPct}
          label="Search APIs (Brave / OpenRouter)"
          sublabel={
            progress?.current_api
              ? `Active: ${progress.current_api}`
              : `${queriesDone} of ${queriesQueued} queries complete`
          }
        />
      )}

      {isScraping && scrapeTotal > 0 && (
        <Phase1ProgressBar
          className="mb-4 shrink-0"
          percent={scrapePct}
          label="Page downloads (Firecrawl / fallback)"
          sublabel={
            progress?.current_url
              ? `Fetching: ${(progress.current_title || progress.current_url || "").slice(0, 80)}`
              : `${scrapeDone} of ${scrapeTotal} pages`
          }
        />
      )}

      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar space-y-1 font-data-mono text-xs">
        {isScraping ? (
          scrapeSteps.length === 0 ? (
            <p className="text-on-surface-variant py-2">
              {scrapeTotal > 0
                ? `Queued ${scrapeTotal} pages for download…`
                : "Waiting for URLs from search…"}
            </p>
          ) : (
            scrapeSteps.map((s, i) => (
              <div
                key={`${s.url}-${i}`}
                className="flex justify-between gap-2 text-on-surface-variant py-0.5"
              >
                <span className="truncate">{s.title || s.url || "page"}</span>
                <span className="text-secondary shrink-0">{s.scrape_via || "ok"}</span>
              </div>
            ))
          )
        ) : (liveLog?.steps || []).length === 0 ? (
          <p className="text-on-surface-variant py-2">{activityLine || copy.detail}</p>
        ) : (
          (liveLog?.steps || []).slice(-16).map((s, i) => (
            <div key={i} className="flex justify-between gap-2 text-on-surface-variant py-0.5">
              <span className="truncate" title={s.query_preview}>
                {s.api || s.engine_label || s.engine || "search"}
                {s.hit_count != null ? ` · ${s.hit_count} URLs` : ""}
              </span>
              <span className={s.status === "error" ? "text-error" : "text-secondary shrink-0"}>
                {s.status}
              </span>
            </div>
          ))
        )}
      </div>
    </section>
  );
}
